import { ExternalLink, ShoppingBag, Info } from "lucide-react";
import SectionHeading from "../components/SectionHeading";

/**
 * Team Store — showcases Hemet Titans apparel curated from Apparel Now.
 * Every "buy" click deep-links to the product page on apparelnow.com so families
 * complete the purchase (and any customization) on the official store.
 * Images are hotlinked to apparelnow.com's create_product endpoint, which returns
 * the actual Titans-branded product render.
 */
const CATALOG_URL = "https://apparelnow.com/hemet-titans-apparel/mens/";

const PRODUCTS = [
  {
    id: "rival-basketball-7-short",
    name: "Rival Basketball 7” Short",
    price: "$26.95",
    category: "Shorts",
    image: "/assets/store/rival-basketball-7-short.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/pants-shorts/rival-basketball-7-short/",
  },
  {
    id: "uv-micropique-polo",
    name: "UV Micropique Colorblock Polo",
    price: "$32.95",
    category: "Polo",
    image: "/assets/store/uv-micropique-polo.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/polos-short-sleeve/uv-micropique-colorblock-polo/",
  },
  {
    id: "core-fleece-crewneck-pocket",
    name: "Core Fleece Crewneck Pocket Sweatshirt",
    price: "$34.95",
    category: "Sweatshirt",
    image: "/assets/store/core-fleece-crewneck-pocket.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/sweatshirts-crewneck/core-fleece-crewneck-pocket-sweatshirt/",
  },
  {
    id: "gildan-ultra-cotton",
    name: "Gildan Ultra Cotton Tee",
    price: "$22.95",
    category: "Shirt",
    image: "/assets/store/gildan-ultra-cotton.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/shirts-short-sleeve/gildan-ultra-cotton/",
  },
  {
    id: "gildan-heavy-blend-hood",
    name: "Gildan Heavy Blend Hood",
    price: "$36.95",
    category: "Hoodie",
    image: "/assets/store/gildan-heavy-blend-hood.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/sweatshirts-hooded/gildan-heavy-blend-hood/",
  },
  {
    id: "gildan-dryblend-fleece-crew",
    name: "Gildan DryBlend Fleece Crew",
    price: "$32.95",
    category: "Sweatshirt",
    image: "/assets/store/gildan-dryblend-fleece-crew.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/sweatshirts-crewneck/gildan-dryblend-fleece-crew/",
  },
  {
    id: "gildan-ultra-cotton-long-sleeve",
    name: "Gildan Ultra Cotton Long-Sleeve",
    price: "$26.95",
    category: "Shirt",
    image: "/assets/store/gildan-ultra-cotton-long-sleeve.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/shirts-long-sleeve/gildan-ultra-cotton-long-sleeve-t-shirt/",
  },
  {
    id: "gildan-heavy-blend-full-zip",
    name: "Gildan Heavy Blend Full-Zip Hood",
    price: "$42.95",
    category: "Hoodie",
    image: "/assets/store/gildan-heavy-blend-full-zip.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/sweatshirts-zip-ups/gildan-heavy-blend-full-zip-hood/",
  },
  {
    id: "unisex-jersey-tank",
    name: "Unisex Jersey Tank",
    price: "$26.95",
    category: "Tank",
    image: "/assets/store/unisex-jersey-tank.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/shirts-sleeveless/unisex-jersey-tank/",
  },
  {
    id: "open-bottom-sweatpants",
    name: "Open-Bottom Sweatpants",
    price: "$39.95",
    category: "Pants",
    image: "/assets/store/open-bottom-sweatpants.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/pants-shorts/open-bottom-sweatpants/",
  },
  {
    id: "vintage-zen-zip-fleece",
    name: "Vintage Zen Full-Zip Fleece Hood",
    price: "$55.95",
    category: "Hoodie",
    image: "/assets/store/vintage-zen-zip-fleece.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/sweatshirts-zip-ups/j-america-adult-adult-vintage-zen-full-zip-fleece-hood/",
  },
  {
    id: "sport-tek-replica-jersey",
    name: "Sport-Tek® PosiCharge® Replica Jersey",
    price: "$28.95",
    category: "Jersey",
    image: "/assets/store/sport-tek-replica-jersey.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/shirts-short-sleeve/sport-tek-posicharge-replica-jersey/",
  },
  {
    id: "heather-contender",
    name: "Heather Colorblock Contender",
    price: "$28.95",
    category: "Performance",
    image: "/assets/store/heather-contender.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/shirts-performance/heather-colorblock-contender/",
  },
  {
    id: "long-sleeve-contender",
    name: "Long-Sleeve Heather Contender™ Tee",
    price: "$30.95",
    category: "Shirt",
    image: "/assets/store/long-sleeve-contender.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/shirts-long-sleeve/long-sleeve-heather-colorblock-contender-tee/",
  },
  {
    id: "camohex-tee",
    name: "CamoHex Tee",
    price: "$28.95",
    category: "Performance",
    image: "/assets/store/camohex-tee.png",
    url: "https://apparelnow.com/hemet-titans-apparel/mens/shirts-performance/camohex-tee/",
  },
];

export default function Store() {
  return (
    <div data-testid="store-page" className="bg-[#F5EFE0] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          eyebrow="Titans Team Store · Official Apparel"
          title="Rep The Titans"
          description="Hemet Titans gear — designed, printed, and shipped by Apparel Now. Every purchase supports our program."
        />

        {/* Info banner — explains external checkout */}
        <div data-testid="store-info-banner" className="mb-10 flex items-start gap-4 bg-[#2D5F3F] text-[#FDFBF7] p-5 md:p-6 border-l-8 border-[#B8863F]">
          <Info className="w-6 h-6 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] font-bold opacity-85">How it works</div>
            <p className="mt-1 leading-snug">
              Browse Titans gear here. Tap <span className="font-bold">Buy on Apparel Now</span> on any item and you&apos;ll be sent to
              apparelnow.com to pick size, customize name/number, and check out.
            </p>
          </div>
        </div>

        {/* Product grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-7" data-testid="store-grid">
          {PRODUCTS.map((p) => (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`product-${p.id}`}
              className="group block bg-white border border-black/15 hover:border-[#2D5F3F] hover:-translate-y-1 transition-all shadow-sm hover:shadow-lg"
            >
              <div className="aspect-square bg-[#FAF6EC] overflow-hidden flex items-center justify-center">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.currentTarget.src = "/assets/titans-warrior-logo.png"; e.currentTarget.className = "w-1/2 h-1/2 object-contain opacity-40"; }}
                />
              </div>
              <div className="p-4">
                <div className="font-display text-[10px] uppercase tracking-[0.25em] text-[#2D5F3F] font-bold">{p.category}</div>
                <h3 className="mt-1 font-display font-black uppercase text-[#0A0A0A] leading-tight text-sm md:text-base line-clamp-2">{p.name}</h3>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-display text-lg md:text-xl font-black text-[#0A0A0A]">{p.price}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#2D5F3F] group-hover:underline">
                    Buy <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* View all */}
        <div className="mt-14 bg-white border border-black/15 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="font-display text-xs uppercase tracking-[0.3em] text-[#2D5F3F] font-bold">Looking for more?</div>
            <h3 className="mt-2 font-display text-2xl md:text-3xl font-black uppercase leading-tight">See the full Hemet Titans catalog</h3>
            <p className="mt-2 text-black/70">Womens, youth, cheer, hats, mugs, and more available on the official Apparel Now store.</p>
          </div>
          <a
            href={CATALOG_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="store-view-catalog"
            className="shrink-0 inline-flex items-center gap-2 bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] font-display uppercase tracking-wider font-extrabold px-6 py-4 transition-colors"
          >
            <ShoppingBag className="w-4 h-4" /> Shop Full Catalog <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
